import { useState } from "react";
import type { PlatformTypeName } from "@multi-chat/shared";
import { ChannelBadge } from "../../../components/channel/ChannelBadge.js";
import { cn } from "../../../lib/utils.js";
import { conversationAvatarTone } from "../conversation-avatar-tone.js";
import { conversationInitials } from "../inbox-display.js";

const WRAP_SIZE = {
  sm: "size-9",
  md: "size-[2.375rem]",
  xl: "size-14",
} as const;

const INITIALS_TEXT = {
  sm: "text-[0.8125rem]",
  md: "text-[0.84375rem]",
  xl: "text-[1.1875rem]",
} as const;

export function ConversationAvatar({
  title,
  imageUrl,
  platformType,
  toneSeed,
  size = "md",
  className,
}: {
  title: string;
  imageUrl: string | null;
  platformType: PlatformTypeName;
  /** Stable id (the contact platform profile) that picks the initials tone. */
  toneSeed: number;
  size?: keyof typeof WRAP_SIZE;
  className?: string;
}): React.JSX.Element {
  const initials = conversationInitials(title);
  const trimmedUrl = imageUrl?.trim() || null;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = Boolean(trimmedUrl) && failedUrl !== trimmedUrl;

  return (
    <span className={cn("relative shrink-0 overflow-visible", WRAP_SIZE[size], className)}>
      <span className={cn("block overflow-hidden rounded-full", WRAP_SIZE[size])}>
        {showImage ? (
          <img
            src={trimmedUrl!}
            alt=""
            className="size-full object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setFailedUrl(trimmedUrl)}
          />
        ) : (
          <span
            className={cn(
              "inline-grid size-full place-items-center font-[650] tracking-[-0.01em]",
              conversationAvatarTone(toneSeed),
              INITIALS_TEXT[size],
            )}
            aria-hidden="true"
          >
            {initials}
          </span>
        )}
      </span>
      <ChannelBadge
        platformType={platformType}
        className={cn(
          "ring-[1.5px]",
          size === "xl" && "end-[-2px] bottom-[-2px] size-[18px]",
        )}
      />
    </span>
  );
}
